// Extracted from src/forms/validators.js — validation rules shared between widget and middleware

import type { OrderItem, OrderSource } from './types.js';
import { eventCouponErrors } from './events-coupons.js';

export const isEmpty = (v: unknown): v is undefined | null | '' =>
  v === undefined || v === null || v === '';

export function validateEmail(v: unknown): boolean {
  if (isEmpty(v)) return true;
  if (typeof v !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export function validatePhoneE164OrBlank(v: unknown): boolean {
  if (isEmpty(v)) return true;
  if (typeof v !== 'string') return false;
  return /^\+?[0-9]{7,15}$/.test(v);
}

export function validateRequired(v: unknown): boolean {
  return !isEmpty(v) && String(v).trim().length > 0;
}

export function validateMinLength(v: unknown, min: number): boolean {
  if (isEmpty(v)) return true;
  return typeof v === 'string' && v.length >= min;
}

export function validateMaxLength(v: unknown, max: number): boolean {
  if (isEmpty(v)) return true;
  return typeof v === 'string' && v.length <= max;
}

// ── Shared Utility Validators ────────────────────────────────────────────────

/**
 * Validate an IANA timezone NAME (e.g., "America/New_York", "UTC").
 *
 * #781 — deliberately stricter than `Intl` alone. ECMA-402 also accepts OFFSET
 * time zones ("+02:00", "-0500"), which `Intl.DateTimeFormat` happily
 * constructs — but an offset is NOT a venue timezone: it is frozen, so a venue
 * stored as "+02:00" silently stops observing DST and every displayed time,
 * reminder and availability slot drifts by an hour for half the year. Only a
 * named zone carries the DST rules, so the name shape is required here.
 */
/**
 * The lenient, READ-path check: anything Intl can resolve, offset zones
 * included. Used where a zone is only CONSUMED (availability `tz`), so that
 * tightening `isValidTimezone` for WRITES never turns a stored or host-supplied
 * legacy value into a 400 on the booking path (#781 G3 security flag).
 */
export function isResolvableTimezone(tz: string): boolean {
  if (typeof tz !== 'string' || tz.length === 0 || tz.length > 100) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function isValidTimezone(tz: string): boolean {
  if (typeof tz !== 'string' || tz.length === 0 || tz.length > 100) return false;
  // A zone NAME starts with a letter; an offset zone starts with +/-/digit.
  // (Some real names contain +/- themselves — "Etc/GMT+2" — hence the anchor
  // on the FIRST character only.)
  if (!/^[A-Za-z][A-Za-z0-9_+\-/]*$/.test(tz)) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate an ISO 4217 currency code.
 */
const VALID_CURRENCIES = new Set([
  'usd', 'eur', 'gbp', 'zar', 'aud', 'cad', 'nzd', 'chf', 'jpy',
  'sek', 'nok', 'dkk', 'pln', 'czk', 'huf', 'brl', 'mxn', 'inr',
  'sgd', 'hkd', 'try', 'thb', 'myr', 'php', 'idr', 'krw', 'twd',
  'aed', 'sar', 'qar', 'bdt', 'egp', 'ngn', 'kes', 'ghs', 'rwf',
]);

export function isValidCurrency(c: string): boolean {
  if (typeof c !== 'string') return false;
  return VALID_CURRENCIES.has(c.toLowerCase());
}

// ── Table Management Validators ──────────────────────────────────────────────

export interface ValidationResult {
  ok: boolean;
  errors: string[];
}

function ok(): ValidationResult {
  return { ok: true, errors: [] };
}

function fail(errors: string[]): ValidationResult {
  return { ok: false, errors };
}

export function validateSectionCreate(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (typeof b.name !== 'string' || b.name.trim().length === 0) {
    errors.push('name is required and must be a non-empty string');
  } else if (b.name.trim().length > 50) {
    errors.push('name must be 50 characters or fewer');
  }

  if (b.sortOrder !== undefined && (typeof b.sortOrder !== 'number' || !Number.isFinite(b.sortOrder))) {
    errors.push('sortOrder must be a finite number');
  }

  return errors.length ? fail(errors) : ok();
}

export function validateSectionUpdate(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (b.name !== undefined) {
    if (typeof b.name !== 'string' || b.name.trim().length === 0) {
      errors.push('name must be a non-empty string');
    } else if (b.name.trim().length > 50) {
      errors.push('name must be 50 characters or fewer');
    }
  }

  if (b.sortOrder !== undefined && (typeof b.sortOrder !== 'number' || !Number.isFinite(b.sortOrder))) {
    errors.push('sortOrder must be a finite number');
  }

  return errors.length ? fail(errors) : ok();
}

// #326 (R2.1): additive spatial-geometry fields on a TableDefinition. All
// optional — absence is valid (the table is unplaced and renders in the grid).
// Field names mirror the shared TableDefinition + the tableListV2 mirror.
const TABLE_SHAPE_VALUES = new Set(['round', 'square', 'rect', 'triangle']);

/**
 * Validate the optional spatial geometry on a table body, pushing any errors
 * onto `errors` with the given path prefix. When a geometry field is ABSENT it
 * is skipped (valid); when PRESENT it must type-check. Shared by the table
 * create/update and full-plan validators so the rules stay in one place.
 */
function validateTableGeometry(t: Record<string, unknown>, prefix: string, errors: string[]): void {
  for (const field of ['posX', 'posY', 'rotation', 'width', 'height'] as const) {
    if (t[field] !== undefined && (typeof t[field] !== 'number' || !Number.isFinite(t[field] as number))) {
      errors.push(`${prefix}${field} must be a finite number`);
    }
  }
  if (t.seats !== undefined && (typeof t.seats !== 'number' || !Number.isInteger(t.seats) || (t.seats as number) < 0)) {
    errors.push(`${prefix}seats must be a non-negative integer`);
  }
  if (t.shape !== undefined && (typeof t.shape !== 'string' || !TABLE_SHAPE_VALUES.has(t.shape))) {
    errors.push(`${prefix}shape must be one of round, square, rect, triangle`);
  }
  if ((t.width !== undefined && typeof t.width === 'number' && t.width <= 0)) {
    errors.push(`${prefix}width must be greater than 0`);
  }
  if ((t.height !== undefined && typeof t.height === 'number' && t.height <= 0)) {
    errors.push(`${prefix}height must be greater than 0`);
  }
}

export function validateTableCreate(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (typeof b.name !== 'string' || b.name.trim().length === 0) {
    errors.push('name is required and must be a non-empty string');
  } else if (b.name.trim().length > 50) {
    errors.push('name must be 50 characters or fewer');
  }

  if (typeof b.sectionId !== 'string' || b.sectionId.trim().length === 0) {
    errors.push('sectionId is required');
  }

  if (b.minGuests !== undefined) {
    if (typeof b.minGuests !== 'number' || !Number.isInteger(b.minGuests) || b.minGuests < 1) {
      errors.push('minGuests must be an integer >= 1');
    }
  }

  if (b.maxGuests !== undefined) {
    if (typeof b.maxGuests !== 'number' || !Number.isInteger(b.maxGuests) || b.maxGuests < 1) {
      errors.push('maxGuests must be an integer >= 1');
    }
  }

  const min = typeof b.minGuests === 'number' ? b.minGuests : 1;
  const max = typeof b.maxGuests === 'number' ? b.maxGuests : min;
  if (Number.isInteger(min) && Number.isInteger(max) && max < min) {
    errors.push('maxGuests must be >= minGuests');
  }

  if (b.online !== undefined && typeof b.online !== 'boolean') {
    errors.push('online must be a boolean');
  }

  if (b.sortOrder !== undefined && (typeof b.sortOrder !== 'number' || !Number.isFinite(b.sortOrder))) {
    errors.push('sortOrder must be a finite number');
  }

  validateTableGeometry(b, '', errors); // #326: optional spatial geometry

  return errors.length ? fail(errors) : ok();
}

export function validateTableUpdate(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (b.name !== undefined) {
    if (typeof b.name !== 'string' || b.name.trim().length === 0) {
      errors.push('name must be a non-empty string');
    } else if (b.name.trim().length > 50) {
      errors.push('name must be 50 characters or fewer');
    }
  }

  if (b.sectionId !== undefined && (typeof b.sectionId !== 'string' || b.sectionId.trim().length === 0)) {
    errors.push('sectionId must be a non-empty string');
  }

  if (b.minGuests !== undefined) {
    if (typeof b.minGuests !== 'number' || !Number.isInteger(b.minGuests) || b.minGuests < 1) {
      errors.push('minGuests must be an integer >= 1');
    }
  }

  if (b.maxGuests !== undefined) {
    if (typeof b.maxGuests !== 'number' || !Number.isInteger(b.maxGuests) || b.maxGuests < 1) {
      errors.push('maxGuests must be an integer >= 1');
    }
  }

  if (b.online !== undefined && typeof b.online !== 'boolean') {
    errors.push('online must be a boolean');
  }

  if (b.sortOrder !== undefined && (typeof b.sortOrder !== 'number' || !Number.isFinite(b.sortOrder))) {
    errors.push('sortOrder must be a finite number');
  }

  validateTableGeometry(b, '', errors); // #326: optional spatial geometry

  return errors.length ? fail(errors) : ok();
}

export function validateFloorPlan(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (!Array.isArray(b.sections)) {
    errors.push('sections must be an array');
  } else {
    for (let i = 0; i < b.sections.length; i++) {
      const s = b.sections[i];
      if (!s || typeof s !== 'object') {
        errors.push(`sections[${i}] must be an object`);
        continue;
      }
      if (typeof s.name !== 'string' || s.name.trim().length === 0) {
        errors.push(`sections[${i}].name is required`);
      }
    }
  }

  if (!Array.isArray(b.tables)) {
    errors.push('tables must be an array');
  } else {
    const sectionIds = new Set(
      Array.isArray(b.sections) ? b.sections.filter((s: any) => s?.id).map((s: any) => s.id) : []
    );
    for (let i = 0; i < b.tables.length; i++) {
      const t = b.tables[i] as Record<string, unknown> | null;
      if (!t || typeof t !== 'object') {
        errors.push(`tables[${i}] must be an object`);
        continue;
      }
      if (typeof t.name !== 'string' || (t.name as string).trim().length === 0) {
        errors.push(`tables[${i}].name is required`);
      }
      // #583: '' is the ONE sanctioned "sectionless" sentinel (the #460
      // cold-start seed writes it; readers already tolerate it). Anything else
      // must reference a section defined in THIS document.
      if (typeof t.sectionId !== 'string' || (t.sectionId !== '' && !sectionIds.has(t.sectionId))) {
        errors.push(`tables[${i}].sectionId must be '' (sectionless) or reference an existing section`);
      }
      if (t.minGuests !== undefined && (typeof t.minGuests !== 'number' || !Number.isInteger(t.minGuests) || (t.minGuests as number) < 1)) {
        errors.push(`tables[${i}].minGuests must be an integer >= 1`);
      }
      if (t.maxGuests !== undefined && (typeof t.maxGuests !== 'number' || !Number.isInteger(t.maxGuests) || (t.maxGuests as number) < 1)) {
        errors.push(`tables[${i}].maxGuests must be an integer >= 1`);
      }
      validateTableGeometry(t, `tables[${i}].`, errors); // #326: optional spatial geometry
    }
  }

  return errors.length ? fail(errors) : ok();
}

// ── Working Hours Schedule Validators ────────────────────────────────────────

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_OF_WEEK_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

function isValidDateStr(d: string): boolean {
  if (!DATE_RE.test(d)) return false;
  const parsed = new Date(d + 'T00:00:00Z');
  return !isNaN(parsed.getTime());
}

function mealTimeToMinutes(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value >= 0 && value <= 23) {
    return value * 60;
  }

  if (typeof value === 'string') {
    const v = value.trim();
    const match = v.match(TIME_RE);
    if (!match) return null;
    return Number(match[1]) * 60 + Number(match[2]);
  }

  return null;
}

function validateMealPeriodConfig(meal: unknown, path: string, errors: string[]): void {
  if (!meal || typeof meal !== 'object') {
    errors.push(`${path} must be an object`);
    return;
  }
  const m = meal as Record<string, unknown>;
  if (typeof m.enabled !== 'boolean') {
    errors.push(`${path}.enabled must be a boolean`);
  }
  const startMin = mealTimeToMinutes(m.start);
  const endMin = mealTimeToMinutes(m.end);

  if (startMin == null) {
    errors.push(`${path}.start must be an integer hour 0–23 or HH:mm`);
  }
  if (endMin == null) {
    errors.push(`${path}.end must be an integer hour 0–23 or HH:mm`);
  }
  if (startMin != null && endMin != null && endMin <= startMin) {
    errors.push(`${path}.end must be greater than ${path}.start`);
  }
}

function validateDailyMealsConfig(mealsRaw: unknown, path: string, errors: string[]): void {
  if (!mealsRaw || typeof mealsRaw !== 'object') {
    errors.push(`${path} must be an object`);
    return;
  }
  const meals = mealsRaw as Record<string, unknown>;
  for (const mealName of ['breakfast', 'lunch', 'dinner']) {
    validateMealPeriodConfig(meals[mealName], `${path}.${mealName}`, errors);
  }
}

export function validateWorkingHoursSchedule(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (typeof b.label !== 'string' || b.label.trim().length === 0) {
    errors.push('label is required and must be a non-empty string');
  } else if (b.label.trim().length > 100) {
    errors.push('label must be 100 characters or fewer');
  }

  if (typeof b.startDate !== 'string' || !isValidDateStr(b.startDate)) {
    errors.push('startDate is required in YYYY-MM-DD format');
  }
  if (typeof b.endDate !== 'string' || !isValidDateStr(b.endDate)) {
    errors.push('endDate is required in YYYY-MM-DD format');
  }
  if (typeof b.startDate === 'string' && typeof b.endDate === 'string' && b.endDate < b.startDate) {
    errors.push('endDate must be on or after startDate');
  }

  if (b.dayRules !== undefined) {
    if (!b.dayRules || typeof b.dayRules !== 'object') {
      errors.push('dayRules must be an object with monday..sunday keys');
    } else {
      const dayRules = b.dayRules as Record<string, unknown>;
      for (const day of DAY_OF_WEEK_KEYS) {
        validateDailyMealsConfig(dayRules[day], `dayRules.${day}`, errors);
      }
    }
  } else if (b.meals !== undefined) {
    validateDailyMealsConfig(b.meals, 'meals', errors);
  } else {
    errors.push('dayRules or meals is required');
  }

  return errors.length ? fail(errors) : ok();
}

export function validateWorkingHoursSchedules(body: unknown): ValidationResult {
  if (!Array.isArray(body)) return fail(['Working hours schedules must be an array']);
  const errors: string[] = [];
  for (let i = 0; i < body.length; i++) {
    const result = validateWorkingHoursSchedule(body[i]);
    if (!result.ok) {
      errors.push(...result.errors.map(e => `schedules[${i}]: ${e}`));
    }
  }
  return errors.length ? fail(errors) : ok();
}

// ── Deposit Override Validators ─────────────────────────────────────────────

export function validateDepositOverride(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (typeof b.label !== 'string' || b.label.trim().length === 0) {
    errors.push('label is required and must be a non-empty string');
  } else if (b.label.trim().length > 100) {
    errors.push('label must be 100 characters or fewer');
  }

  if (!Array.isArray(b.dates) || b.dates.length === 0) {
    errors.push('dates must be a non-empty array of YYYY-MM-DD strings');
  } else {
    for (let i = 0; i < b.dates.length; i++) {
      if (typeof b.dates[i] !== 'string' || !isValidDateStr(b.dates[i])) {
        errors.push(`dates[${i}] must be a valid YYYY-MM-DD date`);
      }
    }
  }

  if (typeof b.amountPerPerson !== 'number' || !Number.isInteger(b.amountPerPerson) || b.amountPerPerson <= 0) {
    errors.push('amountPerPerson must be a positive integer (smallest currency unit)');
  }

  return errors.length ? fail(errors) : ok();
}

export function validateDepositConfig(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (typeof b.defaultAmountPerPerson !== 'number' || !Number.isInteger(b.defaultAmountPerPerson) || b.defaultAmountPerPerson < 0) {
    errors.push('defaultAmountPerPerson must be a non-negative integer (smallest currency unit)');
  }

  if (typeof b.currency !== 'string' || !isValidCurrency(b.currency)) {
    errors.push('currency must be a valid ISO 4217 code');
  }

  if (!Array.isArray(b.overrides)) {
    errors.push('overrides must be an array');
  } else {
    for (let i = 0; i < b.overrides.length; i++) {
      const result = validateDepositOverride(b.overrides[i]);
      if (!result.ok) {
        errors.push(...result.errors.map(e => `overrides[${i}]: ${e}`));
      }
    }
  }

  return errors.length ? fail(errors) : ok();
}

// ── Deposit Rule Validators ──────────────────────────────────────────────────

const VALID_DEPOSIT_TYPES = ['security_hold', 'experience'] as const;
const VALID_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const VALID_WINDOWS = ['breakfast', 'lunch', 'dinner', null];

export function validateDepositRule(rule: any, prefix = ''): ValidationResult {
  const errors: string[] = [];
  const p = prefix ? `${prefix}: ` : '';
  if (!rule || typeof rule !== 'object') return fail([`${p}rule must be an object`]);
  if (!rule.label || typeof rule.label !== 'string' || rule.label.trim().length === 0)
    errors.push(`${p}label is required`);
  if (rule.label && rule.label.length > 100)
    errors.push(`${p}label must be 100 characters or fewer`);
  if (!VALID_DEPOSIT_TYPES.includes(rule.depositType))
    errors.push(`${p}depositType must be 'security_hold' or 'experience'`);
  if (!Number.isInteger(rule.minPartySize) || rule.minPartySize < 1)
    errors.push(`${p}minPartySize must be a positive integer`);
  if (!Array.isArray(rule.daysOfWeek) || rule.daysOfWeek.some((d: any) => !VALID_DAYS.includes(d)))
    errors.push(`${p}daysOfWeek must be an array of valid day strings`);
  if (!VALID_WINDOWS.includes(rule.serviceWindow ?? null))
    errors.push(`${p}serviceWindow must be 'breakfast', 'lunch', 'dinner', or null`);
  if (!Number.isInteger(rule.amountCents) || rule.amountCents <= 0)
    errors.push(`${p}amountCents must be a positive integer`);

  // ── Phase 2 (PRD View 11) — optional card-hold / cancellation fields ────────
  // All optional; validated only when present so legacy rules stay valid.
  if (rule.captureMode != null && rule.captureMode !== 'charge' && rule.captureMode !== 'hold')
    errors.push(`${p}captureMode must be 'charge' or 'hold'`);
  if (rule.cancellationWindowHours != null &&
      (!Number.isFinite(rule.cancellationWindowHours) || rule.cancellationWindowHours < 0))
    errors.push(`${p}cancellationWindowHours must be a non-negative number`);
  if (rule.lateCancelFeeCents != null &&
      (!Number.isInteger(rule.lateCancelFeeCents) || rule.lateCancelFeeCents < 0))
    errors.push(`${p}lateCancelFeeCents must be a non-negative integer`);
  if (rule.lateCancelFeeCents != null && Number.isInteger(rule.amountCents) &&
      rule.lateCancelFeeCents > rule.amountCents)
    errors.push(`${p}lateCancelFeeCents cannot exceed amountCents (the held amount)`);
  if (rule.overrideRole != null && rule.overrideRole !== 'manager' && rule.overrideRole !== 'owner')
    errors.push(`${p}overrideRole must be 'manager' or 'owner'`);

  return errors.length ? fail(errors) : ok();
}

export function validateDepositRules(rules: any): ValidationResult {
  if (!Array.isArray(rules)) return fail(['depositRules must be an array']);
  const errors: string[] = [];
  rules.forEach((rule, i) => {
    const result = validateDepositRule(rule, `rules[${i}]`);
    if (!result.ok) errors.push(...(result.errors || []));
  });
  return errors.length ? fail(errors) : ok();
}

// ── Special Event & Ticketing Validators ─────────────────────────────────────

const MAX_TICKET_PRICE_CENTS = 1_000_000; // ceiling on a per-person cover price

/**
 * #601 G3 L1 — a tier id is not just a label key any more: it is a SORT KEY
 * component (`EVTCAP#<eventId>#TIER#<tierId>`). Unbounded, an id containing
 * `#TIER#` could alias one event's tier counter onto another's, and one long
 * enough to push the SK past DynamoDB's 1024-byte key limit would surface as a
 * raw ValidationException on the checkout path — after the payment intent
 * exists. The dashboard mints `tier_<8 hex>` (eventHelpers.js), so this only
 * binds hand-rolled API writes; the config PUT is the trust boundary, not the UI.
 */
const TIER_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const MAX_EVENT_CAPACITY = 100_000;       // sanity ceiling on covers per event

/** Validate an EventTicketing config block (prepaid-ticket events). */
export function validateEventTicketing(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['ticketing must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (typeof b.enabled !== 'boolean') errors.push('ticketing.enabled must be a boolean');

  if (typeof b.currency !== 'string' || !isValidCurrency(b.currency)) {
    errors.push('ticketing.currency must be a valid ISO 4217 code');
  }
  if (typeof b.capacity !== 'number' || !Number.isInteger(b.capacity) || b.capacity < 1 || b.capacity > MAX_EVENT_CAPACITY) {
    errors.push(`ticketing.capacity must be an integer between 1 and ${MAX_EVENT_CAPACITY}`);
  }
  if (typeof b.pricePerPersonCents !== 'number' || !Number.isInteger(b.pricePerPersonCents) || b.pricePerPersonCents < 0 || b.pricePerPersonCents > MAX_TICKET_PRICE_CENTS) {
    errors.push(`ticketing.pricePerPersonCents must be an integer between 0 and ${MAX_TICKET_PRICE_CENTS}`);
  }
  if (b.kidsPricePerPersonCents !== undefined && b.kidsPricePerPersonCents !== null) {
    if (typeof b.kidsPricePerPersonCents !== 'number' || !Number.isInteger(b.kidsPricePerPersonCents) || b.kidsPricePerPersonCents < 0 || b.kidsPricePerPersonCents > MAX_TICKET_PRICE_CENTS) {
      errors.push(`ticketing.kidsPricePerPersonCents must be an integer between 0 and ${MAX_TICKET_PRICE_CENTS}`);
    }
  }
  if (b.minPartySize !== undefined && (typeof b.minPartySize !== 'number' || !Number.isInteger(b.minPartySize) || b.minPartySize < 1)) {
    errors.push('ticketing.minPartySize must be a positive integer');
  }
  if (b.maxPartySize !== undefined && (typeof b.maxPartySize !== 'number' || !Number.isInteger(b.maxPartySize) || b.maxPartySize < 1)) {
    errors.push('ticketing.maxPartySize must be a positive integer');
  }
  if (typeof b.minPartySize === 'number' && typeof b.maxPartySize === 'number' && b.maxPartySize < b.minPartySize) {
    errors.push('ticketing.maxPartySize must be >= ticketing.minPartySize');
  }

  if (b.tiers !== undefined) {
    if (!Array.isArray(b.tiers)) {
      errors.push('ticketing.tiers must be an array');
    } else {
      const ids = new Set<string>();
      b.tiers.forEach((t: unknown, i: number) => {
        if (!t || typeof t !== 'object') { errors.push(`ticketing.tiers[${i}] must be an object`); return; }
        const tt = t as Record<string, unknown>;
        if (typeof tt.id !== 'string' || tt.id.trim().length === 0) errors.push(`ticketing.tiers[${i}].id is required`);
        else if (!TIER_ID_RE.test(tt.id)) errors.push(`ticketing.tiers[${i}].id must be 1-64 characters of A-Z, a-z, 0-9, hyphen or underscore`);
        else if (ids.has(tt.id)) errors.push(`ticketing.tiers[${i}].id is duplicated`);
        else ids.add(tt.id);
        if (typeof tt.label !== 'string' || tt.label.trim().length === 0) errors.push(`ticketing.tiers[${i}].label is required`);
        else if ((tt.label as string).length > 100) errors.push(`ticketing.tiers[${i}].label must be 100 characters or fewer`);
        if (typeof tt.pricePerPersonCents !== 'number' || !Number.isInteger(tt.pricePerPersonCents) || (tt.pricePerPersonCents as number) <= 0 || (tt.pricePerPersonCents as number) > MAX_TICKET_PRICE_CENTS) {
          errors.push(`ticketing.tiers[${i}].pricePerPersonCents must be a positive integer up to ${MAX_TICKET_PRICE_CENTS}`);
        }
        if (tt.description !== undefined && (typeof tt.description !== 'string' || (tt.description as string).length > 280)) {
          errors.push(`ticketing.tiers[${i}].description must be a string of 280 characters or fewer`);
        }
        // #601 — optional HARD sub-allocation. Absent ⇒ the tier draws from the
        // event's shared pool (today's behaviour). Never 0: an unset limit is
        // omitted, because 0 would mean "sell none".
        //
        // `null` is absent too, and must be: the dashboard's "Tickets available"
        // box sends null when a venue leaves it blank, and every event that
        // predates #601 already stores null on each tier. Treating only
        // `undefined` as absent refused the WHOLE event save whenever any tier
        // had no limit — including edits to an unrelated field, with an error
        // naming a box the venue never touched (round-18 T8; the Perdeberg
        // "tier description wat nie wou save nie" report, 2026-08-28).
        if (tt.capacity !== undefined && tt.capacity !== null) {
          if (typeof tt.capacity !== 'number' || !Number.isInteger(tt.capacity) || (tt.capacity as number) < 1) {
            errors.push(`ticketing.tiers[${i}].capacity must be a positive integer`);
          } else if (typeof b.capacity === 'number' && (tt.capacity as number) > (b.capacity as number)) {
            errors.push(`ticketing.tiers[${i}].capacity cannot exceed the event capacity of ${b.capacity}`);
          }
        }
      });

      // #601 — the sum may not promise more seats than the room has. Under-
      // allocation is deliberately NOT an error here: a venue may hold covers
      // back for phone / walk-in bookings. The dashboard surfaces that case as
      // a non-blocking warning (see eventHelpers.tierAllocation).
      const allocated = b.tiers.reduce((sum: number, t: unknown) => {
        const c = (t as Record<string, unknown> | null)?.capacity;
        return sum + (typeof c === 'number' && Number.isInteger(c) && c > 0 ? c : 0);
      }, 0);
      if (typeof b.capacity === 'number' && allocated > (b.capacity as number)) {
        errors.push(
          `ticketing tier limits add up to ${allocated}, which is more than the event's ${b.capacity} covers`,
        );
      }
    }
  }

  // #811 — discount coupons. Rules live in events-coupons.ts (this file is well
  // past the 500-line ceiling, and normalisation + validation + pricing are one
  // unit); the config PUT stays the trust boundary that enforces them.
  errors.push(...eventCouponErrors(b.coupons));

  return errors.length ? fail(errors) : ok();
}

/** Validate a full SpecialEvent (LocationConfig.events[] item), incl. optional ticketing. */
/**
 * #651 — `menuPdfKey` shape rule, shared by the one-off and series validators.
 *
 * The read path (routes/event-menu-pdf.ts) already refuses a key outside the
 * caller's prefix, so this is deliberately belt-and-braces. It exists because
 * the read check is currently the SOLE fence: the moment a second reader
 * appears that forgets it — a Settings preview, an email attachment, an export
 * — an unvalidated stored value becomes a cross-tenant read. Cheaper to never
 * store the bad value.
 *
 * `locationId` is optional only so the validators stay callable without it;
 * every current caller passes it.
 */
function menuPdfKeyErrors(value: unknown, locationId?: string): string[] {
  if (value === undefined || value === '') return [];
  if (typeof value !== 'string') return ['menuPdfKey must be a string'];
  if (value.length > 400) return ['menuPdfKey must be 400 characters or fewer'];
  if (value.includes('..')) return ['menuPdfKey must not contain ".."'];
  if (locationId && !value.startsWith(`${locationId}/`)) {
    return ['menuPdfKey must be an upload belonging to this location'];
  }
  return [];
}

export function validateSpecialEvent(body: unknown, locationId?: string): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (typeof b.name !== 'string' || b.name.trim().length === 0) {
    errors.push('name is required and must be a non-empty string');
  } else if (b.name.trim().length > 120) {
    errors.push('name must be 120 characters or fewer');
  }
  if (typeof b.date !== 'string' || !isValidDateStr(b.date)) {
    errors.push('date is required in YYYY-MM-DD format');
  }
  if (b.start !== undefined && (typeof b.start !== 'string' || !TIME_RE.test(b.start))) {
    errors.push('start must be in HH:mm format');
  }
  if (b.end !== undefined && (typeof b.end !== 'string' || !TIME_RE.test(b.end))) {
    errors.push('end must be in HH:mm format');
  }
  // #650 — validated server-side, not only in the dialog. There is no
  // unknown-key rejection in this validator, so a field with no rule here is a
  // field with no cap at all: an unbounded description would ride straight into
  // the config document and then into every widget payload.
  if (b.description !== undefined && (typeof b.description !== 'string' || b.description.length > 200)) {
    errors.push('description must be a string of 200 characters or fewer');
  }
  errors.push(...menuPdfKeyErrors(b.menuPdfKey, locationId));
  if (b.menuNote !== undefined && (typeof b.menuNote !== 'string' || b.menuNote.length > 500)) {
    errors.push('menuNote must be a string of 500 characters or fewer');
  }
  if (b.depositPerPerson !== undefined && (typeof b.depositPerPerson !== 'number' || !Number.isInteger(b.depositPerPerson) || b.depositPerPerson < 0)) {
    errors.push('depositPerPerson must be a non-negative integer (smallest currency unit)');
  }
  if (b.sections !== undefined && (!Array.isArray(b.sections) || b.sections.some((s: unknown) => typeof s !== 'string'))) {
    errors.push('sections must be an array of strings');
  }
  if (b.active !== undefined && typeof b.active !== 'boolean') {
    errors.push('active must be a boolean');
  }
  if (b.ticketing !== undefined) {
    const r = validateEventTicketing(b.ticketing);
    if (!r.ok) errors.push(...r.errors);
  }

  return errors.length ? fail(errors) : ok();
}

// ── Recurring "Standard Experiences" (#130) ─────────────────────────────────

const VALID_DAYS_OF_WEEK = new Set([
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
]);
const MAX_BLACKOUT_DATES = 366;

/** Validate a RecurrenceRule (daily/weekly cadence, active window, blackout dates). */
export function validateRecurrenceRule(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['recurrence must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (b.freq !== 'daily' && b.freq !== 'weekly') {
    errors.push("recurrence.freq must be 'daily' or 'weekly'");
  }
  if (typeof b.startDate !== 'string' || !isValidDateStr(b.startDate)) {
    errors.push('recurrence.startDate is required in YYYY-MM-DD format');
  }
  if (b.endDate !== undefined) {
    if (typeof b.endDate !== 'string' || !isValidDateStr(b.endDate)) {
      errors.push('recurrence.endDate must be in YYYY-MM-DD format');
    } else if (typeof b.startDate === 'string' && b.endDate < b.startDate) {
      errors.push('recurrence.endDate must be on or after recurrence.startDate');
    }
  }
  if (b.daysOfWeek !== undefined
    && (!Array.isArray(b.daysOfWeek)
      || b.daysOfWeek.some((d: unknown) => typeof d !== 'string' || !VALID_DAYS_OF_WEEK.has(d)))) {
    errors.push('recurrence.daysOfWeek must be an array of weekday names (monday…sunday)');
  }
  if (b.freq === 'weekly' && (!Array.isArray(b.daysOfWeek) || b.daysOfWeek.length === 0)) {
    errors.push("recurrence.daysOfWeek is required and non-empty when freq is 'weekly'");
  }
  if (b.blackoutDates !== undefined) {
    if (!Array.isArray(b.blackoutDates)) {
      errors.push('recurrence.blackoutDates must be an array');
    } else if (b.blackoutDates.length > MAX_BLACKOUT_DATES) {
      errors.push(`recurrence.blackoutDates must have ${MAX_BLACKOUT_DATES} or fewer entries`);
    } else if (b.blackoutDates.some((d: unknown) => typeof d !== 'string' || !isValidDateStr(d))) {
      errors.push('recurrence.blackoutDates must be YYYY-MM-DD strings');
    }
  }

  return errors.length ? fail(errors) : ok();
}

/** Validate a full ExperienceSeries (LocationConfig EXP# item), incl. recurrence + optional ticketing. */
export function validateExperienceSeries(body: unknown, locationId?: string): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (typeof b.name !== 'string' || b.name.trim().length === 0) {
    errors.push('name is required and must be a non-empty string');
  } else if (b.name.trim().length > 120) {
    errors.push('name must be 120 characters or fewer');
  }
  if (b.recurrence === undefined) {
    errors.push('recurrence is required');
  } else {
    const r = validateRecurrenceRule(b.recurrence);
    if (!r.ok) errors.push(...r.errors);
  }
  if (b.start !== undefined && (typeof b.start !== 'string' || !TIME_RE.test(b.start))) {
    errors.push('start must be in HH:mm format');
  }
  if (b.end !== undefined && (typeof b.end !== 'string' || !TIME_RE.test(b.end))) {
    errors.push('end must be in HH:mm format');
  }
  // #650 — validated server-side, not only in the dialog. There is no
  // unknown-key rejection in this validator, so a field with no rule here is a
  // field with no cap at all: an unbounded description would ride straight into
  // the config document and then into every widget payload.
  if (b.description !== undefined && (typeof b.description !== 'string' || b.description.length > 200)) {
    errors.push('description must be a string of 200 characters or fewer');
  }
  errors.push(...menuPdfKeyErrors(b.menuPdfKey, locationId));
  if (b.menuNote !== undefined && (typeof b.menuNote !== 'string' || b.menuNote.length > 500)) {
    errors.push('menuNote must be a string of 500 characters or fewer');
  }
  if (b.depositPerPerson !== undefined
    && (typeof b.depositPerPerson !== 'number' || !Number.isInteger(b.depositPerPerson) || b.depositPerPerson < 0)) {
    errors.push('depositPerPerson must be a non-negative integer (smallest currency unit)');
  }
  if (b.sections !== undefined
    && (!Array.isArray(b.sections) || b.sections.some((s: unknown) => typeof s !== 'string'))) {
    errors.push('sections must be an array of strings');
  }
  if (b.active !== undefined && typeof b.active !== 'boolean') {
    errors.push('active must be a boolean');
  }
  if (b.ticketing !== undefined) {
    const r = validateEventTicketing(b.ticketing);
    if (!r.ok) errors.push(...r.errors);
  }

  return errors.length ? fail(errors) : ok();
}

// ── Booking / Availability / Payment Validators ─────────────────────────────

const MAX_PAYMENT_AMOUNT = 1_000_000; // Maximum amount in smallest currency unit

/**
 * Validate GET /availability query params.
 */
export function validateAvailabilityRequest(query: Record<string, unknown>): ValidationResult {
  const errors: string[] = [];
  const { locationId, calendarId, date, tz } = query;

  if (!locationId || typeof locationId !== 'string' || locationId.length > 100) {
    errors.push('locationId is required (max 100 chars)');
  }
  // calendarId is optional for standalone (DynamoDB) locations
  if (calendarId && (typeof calendarId !== 'string' || calendarId.length > 100)) {
    errors.push('calendarId must be a string (max 100 chars)');
  }
  if (!date || typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    errors.push('date is required in YYYY-MM-DD format');
  }
  if (!tz || typeof tz !== 'string') {
    errors.push('tz (timezone) is required');
  } else if (!isResolvableTimezone(String(tz))) {
    // Read path stays lenient — see isResolvableTimezone.
    errors.push('tz must be a valid IANA timezone (e.g., "America/New_York")');
  }

  return errors.length ? fail(errors) : ok();
}

/**
 * Validate a booking slot's start/end times: both parseable ISO-8601, end
 * strictly after start, and start not in the past (small grace for clock skew).
 * Pushes messages into `errors`. See issue #291.
 */
export function validateSlotTimes(
  start: unknown,
  end: unknown,
  errors: string[],
  startLabel = 'slot.start',
  endLabel = 'slot.end',
): void {
  const PAST_GRACE_MS = 5 * 60 * 1000;
  const startMs = typeof start === 'string' && start ? Date.parse(start) : NaN;
  const endMs = typeof end === 'string' && end ? Date.parse(end) : NaN;

  if (typeof start !== 'string' || !start) {
    errors.push(`${startLabel} is required`);
  } else if (isNaN(startMs)) {
    errors.push(`${startLabel} must be a valid ISO-8601 date string`);
  }
  if (typeof end !== 'string' || !end) {
    errors.push(`${endLabel} is required`);
  } else if (isNaN(endMs)) {
    errors.push(`${endLabel} must be a valid ISO-8601 date string`);
  }
  if (!isNaN(startMs) && !isNaN(endMs) && endMs <= startMs) {
    errors.push(`${endLabel} must be after ${startLabel}`);
  }
  if (!isNaN(startMs) && startMs < Date.now() - PAST_GRACE_MS) {
    errors.push(`${startLabel} must not be in the past`);
  }
}

/**
 * Validate POST /book request body.
 */
export function validateBookingRequest(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  // locationId — validated separately by enforceLocationId middleware
  if (b.locationId !== undefined && (typeof b.locationId !== 'string' || b.locationId.length > 100)) {
    errors.push('locationId must be a string (max 100 chars)');
  }

  // calendarId
  if (typeof b.calendarId !== 'string' || b.calendarId.length === 0 || b.calendarId.length > 100) {
    errors.push('calendarId is required (max 100 chars)');
  }

  // slot
  if (!b.slot || typeof b.slot !== 'object') {
    errors.push('slot is required with start and end ISO-8601 strings');
  } else {
    const slot = b.slot as Record<string, unknown>;
    validateSlotTimes(slot.start, slot.end, errors);
  }

  // contact
  if (!b.contact || typeof b.contact !== 'object') {
    errors.push('contact is required');
  } else {
    const c = b.contact as Record<string, unknown>;
    if (typeof c.firstName !== 'string' || c.firstName.trim().length === 0) {
      errors.push('contact.firstName is required');
    } else if (c.firstName.length > 100) {
      errors.push('contact.firstName must be 100 characters or fewer');
    }
    if (c.lastName !== undefined && typeof c.lastName === 'string' && c.lastName.length > 100) {
      errors.push('contact.lastName must be 100 characters or fewer');
    }
    if (typeof c.email !== 'string' || c.email.trim().length === 0) {
      errors.push('contact.email is required');
    } else if (!validateEmail(c.email)) {
      errors.push('contact.email must be a valid email address');
    }
    if (c.phone !== undefined && typeof c.phone === 'string' && c.phone.length > 0 && !validatePhoneE164OrBlank(c.phone)) {
      errors.push('contact.phone must be E.164 format (e.g., +27821234567)');
    }
  }

  // partySize
  if (b.partySize !== undefined) {
    if (typeof b.partySize !== 'number' || !Number.isInteger(b.partySize) || b.partySize < 1 || b.partySize > 200) {
      errors.push('partySize must be an integer between 1 and 200');
    }
  }

  // kidsCount (optional, must be non-negative and <= partySize)
  if (b.kidsCount !== undefined) {
    if (typeof b.kidsCount !== 'number' || !Number.isInteger(b.kidsCount) || b.kidsCount < 0 || b.kidsCount > 200) {
      errors.push('kidsCount must be a non-negative integer up to 200');
    }
  }

  // highChairNeeded (optional boolean)
  if (b.highChairNeeded !== undefined && typeof b.highChairNeeded !== 'boolean') {
    errors.push('highChairNeeded must be a boolean');
  }
  // dietaryDetails (optional free text, max 500 chars)
  if (b.dietaryDetails !== undefined) {
    if (typeof b.dietaryDetails !== 'string') {
      errors.push('dietaryDetails must be a string');
    } else if (b.dietaryDetails.length > 500) {
      errors.push('dietaryDetails must be 500 characters or fewer');
    }
  }

  // notes
  if (b.notes !== undefined && typeof b.notes === 'string' && b.notes.length > 2000) {
    errors.push('notes must be 2000 characters or fewer');
  }

  return errors.length ? fail(errors) : ok();
}

/**
 * Validate POST /api/voice/book request body.
 * Like validateBookingRequest but phone is required and email is optional.
 */
export function validateVoiceBookingRequest(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (typeof b.locationId !== 'string' || b.locationId.trim().length === 0) {
    errors.push('locationId is required');
  }

  validateSlotTimes(b.slotStart, b.slotEnd, errors, 'slotStart', 'slotEnd');

  if (!b.contact || typeof b.contact !== 'object') {
    errors.push('contact is required');
  } else {
    const c = b.contact as Record<string, unknown>;
    if (typeof c.firstName !== 'string' || c.firstName.trim().length === 0) {
      errors.push('contact.firstName is required');
    } else if (c.firstName.length > 100) {
      errors.push('contact.firstName must be 100 characters or fewer');
    }
    if (c.lastName !== undefined && typeof c.lastName === 'string' && c.lastName.length > 100) {
      errors.push('contact.lastName must be 100 characters or fewer');
    }
    if (typeof c.phone !== 'string' || c.phone.trim().length === 0) {
      errors.push('contact.phone is required');
    } else if (!validatePhoneE164OrBlank(c.phone)) {
      errors.push('contact.phone must be E.164 format (e.g., +12025550123)');
    }
    if (c.email !== undefined && c.email !== '' && typeof c.email === 'string' && !validateEmail(c.email)) {
      errors.push('contact.email must be a valid email address');
    }
  }

  if (b.partySize !== undefined) {
    if (typeof b.partySize !== 'number' || !Number.isInteger(b.partySize) || b.partySize < 1 || b.partySize > 200) {
      errors.push('partySize must be an integer between 1 and 200');
    }
  }

  if (b.notes !== undefined && typeof b.notes === 'string' && b.notes.length > 2000) {
    errors.push('notes must be 2000 characters or fewer');
  }

  return errors.length ? fail(errors) : ok();
}

/**
 * Validate payment amount and currency (shared by create-payment-intent and create-ghl-order).
 */
export function validatePaymentRequest(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') return fail(['Request body must be an object']);
  const b = body as Record<string, unknown>;
  const errors: string[] = [];

  if (typeof b.amount !== 'number' || !Number.isInteger(b.amount) || b.amount <= 0) {
    errors.push('amount must be a positive integer (smallest currency unit)');
  } else if (b.amount > MAX_PAYMENT_AMOUNT) {
    errors.push(`amount must not exceed ${MAX_PAYMENT_AMOUNT}`);
  }

  if (typeof b.currency !== 'string' || !b.currency) {
    errors.push('currency is required');
  } else if (!isValidCurrency(b.currency)) {
    errors.push('currency must be a valid ISO 4217 code (e.g., "usd", "zar")');
  }

  return errors.length ? fail(errors) : ok();
}

// ── Order Create Validator (Epic 3 W0.1) ────────────────────────────────────

const ORDER_SOURCES: readonly OrderSource[] = ['in-table', 'voice', 'staff'];
// Format gate: must start with `+`, digit, or `(` (not whitespace) and end on
// a digit (not whitespace or a stray separator). Total length 7–20 chars.
// Digit-count check (≥7, ≤15 per ITU E.164) runs after this to reject
// all-zero / too-short / too-long sequences that happen to fit the character class.
const PHONE_FORMAT_RE = /^(?:\+?[0-9]|\()[0-9\s()-]{5,18}[0-9]$/;

function isValidOrderPhone(value: string): boolean {
  if (!PHONE_FORMAT_RE.test(value)) return false;
  const digits = (value.match(/\d/g) || []).length;
  if (digits < 7 || digits > 15) return false;
  // Reject all-zero digit strings (e.g., "+0000000") — not a real number.
  if (/^0+$/.test(value.replace(/\D/g, ''))) return false;
  return true;
}

/**
 * Subset of `Order` accepted from clients when creating a new order.
 * Server computes `orderId`, `status` ('pending'), `subtotalCents`, and timestamps.
 */
// Duplicated (not Omit<Order, ...>) so the client contract is explicit and immune to future Order field additions.
export interface OrderCreateRequest {
  locationId: string;
  tableId: string;
  source: OrderSource;
  items: OrderItem[];
  guestName?: string;
  guestPhone?: string;
  voiceCallId?: string;
}

/**
 * Parse-and-validate a POST /api/order/create body.
 *
 * Unlike the other validators in this file (which return ValidationResult),
 * this one PARSES — on success it returns the canonical `value` with unknown
 * fields stripped, safe for direct DynamoDB insertion alongside server-computed
 * fields (orderId, status, subtotalCents, timestamps).
 *
 * Rejects with a human-readable `errors: string[]` on any validation failure.
 */
export function validateOrderCreateRequest(
  input: unknown,
): { ok: true; value: OrderCreateRequest } | { ok: false; errors: string[] } {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, errors: ['Request body must be an object'] };
  }
  const b = input as Record<string, unknown>;
  const errors: string[] = [];

  // locationId
  const locationIdValid =
    typeof b.locationId === 'string' && b.locationId.trim().length > 0;
  if (!locationIdValid) {
    errors.push('locationId is required and must be a non-empty string');
  } else if ((b.locationId as string).trim().length > 100) {
    errors.push('locationId must be 100 characters or less');
  }

  // tableId
  const tableIdValid =
    typeof b.tableId === 'string' && b.tableId.trim().length > 0;
  if (!tableIdValid) {
    errors.push('tableId is required and must be a non-empty string');
  } else if ((b.tableId as string).trim().length > 100) {
    errors.push('tableId must be 100 characters or less');
  }

  // source
  const sourceValid =
    typeof b.source === 'string' && (ORDER_SOURCES as readonly string[]).includes(b.source);
  if (!sourceValid) {
    errors.push(`source is required and must be one of: ${ORDER_SOURCES.join(', ')}`);
  }

  // items
  const cleanItems: OrderItem[] = [];
  if (!Array.isArray(b.items)) {
    errors.push('items is required and must be a non-empty array');
  } else if (b.items.length === 0) {
    errors.push('items must not be empty');
  } else if (b.items.length > 100) {
    errors.push('items must contain 100 or fewer entries');
  } else {
    for (let i = 0; i < b.items.length; i++) {
      const raw = b.items[i];
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        errors.push(`items[${i}] must be an object`);
        continue;
      }
      const it = raw as Record<string, unknown>;
      let itemOk = true;

      if (typeof it.itemId !== 'string' || it.itemId.trim().length === 0) {
        errors.push(`items[${i}].itemId is required and must be a non-empty string`);
        itemOk = false;
      } else if (it.itemId.trim().length > 100) {
        errors.push(`items[${i}].itemId must be 100 characters or less`);
        itemOk = false;
      }
      if (typeof it.name !== 'string' || it.name.trim().length === 0) {
        errors.push(`items[${i}].name is required and must be a non-empty string`);
        itemOk = false;
      } else if (it.name.trim().length > 200) {
        errors.push(`items[${i}].name must be 200 characters or less`);
        itemOk = false;
      }
      if (
        typeof it.priceAtOrder !== 'number' ||
        !Number.isInteger(it.priceAtOrder) ||
        (it.priceAtOrder as number) < 0
      ) {
        errors.push(
          `items[${i}].priceAtOrder must be a non-negative integer (smallest currency unit)`,
        );
        itemOk = false;
      }
      if (
        typeof it.quantity !== 'number' ||
        !Number.isInteger(it.quantity) ||
        (it.quantity as number) < 1
      ) {
        errors.push(`items[${i}].quantity must be an integer >= 1`);
        itemOk = false;
      }

      let cleanModifiers: string[] | undefined;
      if (it.modifiers !== undefined) {
        if (!Array.isArray(it.modifiers)) {
          errors.push(`items[${i}].modifiers must be an array of strings`);
          itemOk = false;
        } else if (it.modifiers.length > 20) {
          errors.push(`items[${i}].modifiers must contain 20 or fewer entries`);
          itemOk = false;
        } else {
          const badIdx = (it.modifiers as unknown[]).findIndex(m => typeof m !== 'string');
          if (badIdx !== -1) {
            errors.push(`items[${i}].modifiers[${badIdx}] must be a string`);
            itemOk = false;
          } else {
            cleanModifiers = it.modifiers as string[];
          }
        }
      }

      let cleanNotes: string | undefined;
      if (it.notes !== undefined) {
        if (typeof it.notes !== 'string') {
          errors.push(`items[${i}].notes must be a string`);
          itemOk = false;
        } else if (it.notes.length > 500) {
          errors.push(`items[${i}].notes must be 500 characters or fewer`);
          itemOk = false;
        } else {
          cleanNotes = it.notes;
        }
      }

      if (itemOk) {
        const cleanItem: OrderItem = {
          itemId: (it.itemId as string).trim(),
          name: (it.name as string).trim(),
          priceAtOrder: it.priceAtOrder as number,
          quantity: it.quantity as number,
        };
        if (cleanModifiers !== undefined) cleanItem.modifiers = cleanModifiers;
        if (cleanNotes !== undefined) cleanItem.notes = cleanNotes;
        cleanItems.push(cleanItem);
      }
    }
  }

  // guestName (optional)
  let cleanGuestName: string | undefined;
  if (b.guestName !== undefined) {
    if (typeof b.guestName !== 'string' || b.guestName.trim().length === 0) {
      errors.push('guestName, when provided, must be a non-empty string');
    } else if (b.guestName.length > 200) {
      errors.push('guestName must be 200 characters or fewer');
    } else {
      cleanGuestName = b.guestName;
    }
  }

  // guestPhone (optional)
  let cleanGuestPhone: string | undefined;
  if (b.guestPhone !== undefined) {
    if (typeof b.guestPhone !== 'string' || !isValidOrderPhone(b.guestPhone)) {
      errors.push(
        'guestPhone, when provided, must contain 7–15 digits (E.164 range) with optional spaces, hyphens, parentheses, or a leading +',
      );
    } else {
      cleanGuestPhone = b.guestPhone;
    }
  }

  // voiceCallId (optional)
  let cleanVoiceCallId: string | undefined;
  if (b.voiceCallId !== undefined) {
    if (typeof b.voiceCallId !== 'string' || b.voiceCallId.trim().length === 0) {
      errors.push('voiceCallId, when provided, must be a non-empty string');
    } else {
      cleanVoiceCallId = b.voiceCallId;
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  const value: OrderCreateRequest = {
    locationId: (b.locationId as string).trim(),
    tableId: (b.tableId as string).trim(),
    source: b.source as OrderSource,
    items: cleanItems,
  };
  if (cleanGuestName !== undefined) value.guestName = cleanGuestName;
  if (cleanGuestPhone !== undefined) value.guestPhone = cleanGuestPhone;
  if (cleanVoiceCallId !== undefined) value.voiceCallId = cleanVoiceCallId;

  return { ok: true, value };
}

/**
 * #427 / G3 dynamo-schema BLOCK — `brandConfig` must be a plain object.
 *
 * Before #427, `patchConfig` SET `brandConfig` as a whole value, so a row that
 * somehow held `null` or a string healed itself on the next save. #427 writes it
 * field by field behind an `if_not_exists` seed — and **a seed guards ABSENCE,
 * never a WRONG TYPE**. A non-Map `brandConfig` therefore makes every subsequent
 * branding write fail with a DynamoDB ValidationException, permanently, with no
 * API path able to repair it.
 *
 * `brandConfig` was the one tenant-authored block on these routes with no
 * validator, while `automations`, `events`, `customFieldDefs`, `referralPartners`
 * and `workingHoursSchedules` all had one. That gap was survivable before #427
 * and is not after it.
 *
 * Also rejects an empty-string key, which would otherwise produce an empty
 * `ExpressionAttributeNames` entry and a 500.
 */
export function validateBrandConfig(v: unknown): { ok: true } | { ok: false; errors: string[] } {
  if (v === undefined) return { ok: true };
  if (v === null || typeof v !== 'object' || Array.isArray(v)) {
    return { ok: false, errors: ['brandConfig must be an object'] };
  }
  const errors: string[] = [];
  for (const key of Object.keys(v as Record<string, unknown>)) {
    if (key.trim() === '') errors.push('brandConfig keys must not be empty');
  }
  return errors.length > 0 ? { ok: false, errors } : { ok: true };
}
